import { useEffect, useRef } from 'react'

import { useRouter } from '@tanstack/react-router'

import {
  initGleap,
  isSupportChatDisabled,
  setupGleapVisibility,
  shouldInitGleap,
} from '@/integrations/gleap'
import { useContextStore } from '@/stores/context'
import { useWhiteLabelStore } from '@/stores/white-label'

/**
 * Bootstraps Gleap, skipping whoever opted out of the support chat so the SDK
 * chunk is never downloaded for them. See `isSupportChatDisabled` for which
 * account answers before and after login.
 *
 * Both stores are persisted and `RootLayout` holds the pre-auth render until the
 * public white-label settings land, so the deciding flag is already in hand by
 * the time this mounts — no bubble flashes before we know to hide it.
 *
 * On a branded domain the chat is withheld until someone is logged in: only
 * then does an account answer for itself. A tenant that keeps the chat on still
 * gets it on its own domain once its users are in.
 *
 * Renders nothing.
 */
export function GleapBootstrap() {
  const router = useRouter()
  const accountFlag = useContextStore(
    (state) =>
      state.userAccountContext?.accountInformation.hasSupportChatDisabled,
  )
  const brandedDomainFlag = useWhiteLabelStore(
    (state) => state.hasSupportChatDisabled,
  )
  const isBrandedDomain = useWhiteLabelStore((state) => state.isWhiteLabel)
  const hasSupportChatDisabled = isSupportChatDisabled(
    accountFlag,
    brandedDomainFlag,
    isBrandedDomain,
  )

  const initializedRef = useRef(false)

  useEffect(() => {
    if (initializedRef.current) return
    if (hasSupportChatDisabled) return
    if (!shouldInitGleap(window.location.pathname)) return

    initializedRef.current = true
    // `initGleap` now downloads the SDK chunk lazily and returns a
    // promise. Chain visibility setup so the first `evaluate()` call
    // sees `gleapInitialized === true`; otherwise it'd be a silent
    // no-op and the bubble wouldn't surface until the next route nav.
    void initGleap().then(() => {
      setupGleapVisibility(router)
    })
  }, [router, hasSupportChatDisabled])

  return null
}
